import type { PcMonitorListing } from '../../types/pcmonitor.ts';
import { formatChf } from './listingMath.ts';
import ListingImage from './ListingImage.tsx';
import AskingPrice from './AskingPrice.tsx';
import ValueGap from './ValueGap.tsx';
import ListingAge from './ListingAge.tsx';
import PriceDrop from './PriceDrop.tsx';
import VerdictBadge from './VerdictBadge.tsx';
import ListingFlags from './ListingFlags.tsx';
import { AdvertLink, ProviderLine } from './ListingRow.tsx';

interface ListingCardProps {
  listing: PcMonitorListing;
}

/**
 * Mobile card (below md) — the operator reads this from a phone far more
 * than at a desk, so the stacked layout leads with photo, price and gap.
 */
export default function ListingCard({ listing }: ListingCardProps) {
  return (
    <div className="border border-gray-200 rounded-lg bg-white p-3 space-y-2.5 shadow-sm">
      <div className="flex gap-3">
        <a href={listing.url} target="_blank" rel="noopener noreferrer" aria-label={listing.title}>
          <ListingImage thumbnailUrl={listing.thumbnail_url} title={listing.title} className="w-24 h-24" />
        </a>
        <div className="min-w-0 space-y-1">
          <AdvertLink listing={listing} className="text-sm leading-snug line-clamp-3" />
          <ProviderLine listing={listing} />
          <ListingFlags
            highPriority={listing.high_priority}
            cardOnly={listing.card_only}
            flags={listing.flags}
          />
        </div>
      </div>
      <div className="flex items-end justify-between gap-2 flex-wrap">
        <div className="space-y-0.5">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Asking</div>
          <AskingPrice listing={listing} />
        </div>
        <div className="space-y-0.5 text-right">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Est. value</div>
          <div className="text-sm font-semibold tabular-nums text-gray-700">
            {listing.estimated_value != null ? formatChf(listing.estimated_value) : '—'}
          </div>
        </div>
        <div className="space-y-0.5 text-right">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Gap</div>
          <ValueGap listing={listing} />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs text-gray-500">
          Age <ListingAge firstSeen={listing.first_seen} />
        </span>
        <PriceDrop history={listing.price_history} />
        <VerdictBadge verdict={listing.verdict} rejectionReasons={listing.rejection_reasons} />
      </div>
    </div>
  );
}
